import clsx from 'clsx'
import PropTypes from 'prop-types'
import React from 'react'
import { translate } from 'react-i18next'
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete'
import Coachmark from 'components/common/coachmark'
import { COACHMARK_POSITION } from 'components/common/coachmark/const'
import Icon from 'components/common/icon'
import ICONS, { ICON_SIZE } from 'components/common/icon/const'
import TextField from 'components/common/textfield'
import Loader from 'components/loader'
import BlockedLocationModal from 'components/modal/blocked-location-modal'
import BackdropModal from 'components/portal/backdrop-modal'
import Typo from 'constants/typography'
import StateStorage from 'lib/state-storage'
import styles from './style.scss'

const GEO_STATE = {
  denied: 'denied',
  granted: 'granted',
  prompt: 'prompt',
}
const GOOGLE_MAP_SCRIPT_LOAD_CALLBACK = 'onMapScriptLoaded'

class SearchInput extends React.PureComponent {
  searchInputEl = React.createRef()

  constructor() {
    super()
    this.state = {
      geoAvailable: 'geolocation' in navigator,
      geoFetching: false,
      geoState: GEO_STATE.prompt,
      inputLocked: false,
      showLocationPermissionModal: false,
      value: '',
    }
    this.geoOptions = {
      enableHighAccuracy: true,
      maximumAge: 5000, // Location cache lifespan
      timeout: 30000, // Will have to return after it takes too long
    }
  }

  componentDidMount() {
    const { geoAvailable } = this.state
    // Automatically set base location to user's location if the permission is already granted
    if (geoAvailable && navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((status) => {
        if (status.state === GEO_STATE.granted) {
          this.geoGetUserLocation()
        }

        this.setState({ geoState: status.state })
      })
    }
  }

  isGoogleMapsLoaded() {
    return window && window.google && window.google.maps
  }

  onCoachMarkClosed = () => {
    StateStorage.setPickupCoachMarkShown(true)
    const { setCoachMark } = this.props
    setCoachMark(false)
  }

  onSuggestionSelected = (placeName) => {
    const { geoUpdate, showUnavailable } = this.props
    this.setState({
      inputLocked: true,
      value: placeName,
      geoState: GEO_STATE.prompt,
    })

    // Clear all the distances
    geoUpdate(null)

    /* istanbul ignore if */
    if (this.searchInputEl.current) {
      this.searchInputEl.current.blur()
    }

    geocodeByAddress(placeName)
      .then((res) => getLatLng(res[0]))
      .then((latLng) =>
        geoUpdate({ latitude: latLng.lat, longitude: latLng.lng }),
      )
      .catch(() => showUnavailable())
  }

  geoGetUserLocation() {
    const { geoFetching, geoState } = this.state

    const { geoUpdate, setCurrentLocation } = this.props

    if (geoFetching || geoState === GEO_STATE.denied) return

    this.setState({ geoFetching: true })
    navigator.geolocation.getCurrentPosition(
      (res) => {
        this.setState({
          geoFetching: false,
          geoState: GEO_STATE.granted,
          value: '',
        })
        geoUpdate(res.coords)
        setCurrentLocation({
          latitude: res.coords.latitude,
          longitude: res.coords.longitude,
        })
      },
      () => this.setState({ geoFetching: false, geoState: GEO_STATE.denied }),
      this.geoOptions,
    )
  }

  clearLocation() {
    const { geoUpdate } = this.props
    this.setState({
      inputLocked: true,
      value: '',
      geoState: GEO_STATE.prompt,
    })
    geoUpdate(null)
  }

  toggleShowLocationPermissionModal = (e) => {
    if (e?.preventDefault) {
      /* istanbul ignore next */
      e.preventDefault()
    }

    this.setState(({ showLocationPermissionModal }) => ({
      showLocationPermissionModal: !showLocationPermissionModal,
    }))
  }

  handleLocationClick = () => {
    const { geoState } = this.state

    if (geoState && geoState !== GEO_STATE.denied) {
      this.geoGetUserLocation()
    } else {
      this.toggleShowLocationPermissionModal()
    }
  }

  renderLocationTracking() {
    const { t } = this.props
    const { geoState, showLocationPermissionModal } = this.state

    if (geoState && geoState === GEO_STATE.granted) {
      return (
        <div className="SI__location-tracking-area">
          <hr className="SI__separator-line" />
          <Icon
            src={ICONS.webCurrentLocationDisabled}
            size={ICON_SIZE.small}
            className="location-tracking-icon-disabled"
          />
          <span className={clsx(Typo.overline, 'SI__span-nearyby__tracked')}>
            {t('Tracked')}
          </span>
        </div>
      )
    }

    return (
      <React.Fragment>
        <div
          className="SI__location-tracking-area"
          onClick={this.handleLocationClick}
          role="presentation"
        >
          <hr className="SI__separator-line" />
          <Icon
            size={ICON_SIZE.small}
            src={ICONS.webCurrentLocation}
            className="location-tracking-icon"
          />
          <span className={clsx(Typo.overline, 'SI__span-nearyby')}>
            {t('Nearby')}
          </span>
        </div>
        <BackdropModal
          onBackdropClick={this.toggleShowLocationPermissionModal}
          isOpen={showLocationPermissionModal}
        >
          <BlockedLocationModal
            close={this.toggleShowLocationPermissionModal}
          />
        </BackdropModal>
      </React.Fragment>
    )
  }

  handleInputChange(res) {
    if (res) {
      this.setState({ value: res, inputLocked: false })
    }

    if (res.length === 0) {
      this.clearLocation()
    }
  }

  /* istanbul ignore next */
  renderSuggestion(item, index, getSuggestionItemProps) {
    const { t } = this.props

    const suggestionProps = getSuggestionItemProps(item, {
      className: `search__result-item${item.active ? ' hover' : ''}`,
      onMouseDown: () => this.onSuggestionSelected(item.description),
    })

    return (
      <li {...suggestionProps} key={`tmp-${index}`}>
        <Icon
          className="icon"
          src={ICONS.pickUpLocation}
          size={ICON_SIZE.medium}
        />
        <div className={clsx(Typo.caption, 'suggestion-description')}>
          {t(item.description)}
        </div>
      </li>
    )
  }

  getCoachMarkStatus = () => {
    const coachMarkAlreadyShown = StateStorage.isPickupCoachMarkShown()
    const { showCoachMark } = this.props

    return !coachMarkAlreadyShown && showCoachMark
  }

  render() {
    const { state } = this
    const { inputLocked, value } = state
    const { t } = this.props

    const searchActiveClasses = clsx({
      search__input: true,
      selected: inputLocked,
    })
    const searchResultClasses = clsx({
      search__result: true,
      active: !inputLocked,
    })

    // This bounds data is the same with the apps
    if (this.isGoogleMapsLoaded() && !this.searchOptions) {
      this.searchOptions = {
        bounds: new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(13.447785, 100.103168),
          new window.google.maps.LatLng(14.340953, 101.31422),
        ),
      }
    }

    return (
      <PlacesAutocomplete
        value={t(value)}
        searchOptions={this.searchOptions}
        onChange={(res) => this.handleInputChange(res)}
        onSelect={(res) => this.onSuggestionSelected(res)}
        onError={(_, clearSuggestions) => clearSuggestions()}
        googleCallbackName={GOOGLE_MAP_SCRIPT_LOAD_CALLBACK}
        shouldFetchSuggestions
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <React.Fragment>
            <style jsx>{styles}</style>
            <div className={searchActiveClasses}>
              <TextField
                fullWidth
                className="search-field"
                prefix={
                  <Icon
                    className="placeholder-search-icon"
                    src={ICONS.search}
                    size={ICON_SIZE.medium}
                  />
                }
                postfix={
                  value ? (
                    <Icon
                      className="close-icon"
                      src={ICONS.close}
                      size={ICON_SIZE.medium}
                      onClick={() => this.clearLocation()}
                    />
                  ) : undefined
                }
                hideUnderline
                inputRef={this.searchInputEl}
                inputProps={getInputProps({
                  type: 'text',
                  placeholder: t('Try your office postal code.'),
                })}
              />
              <Coachmark
                onClose={this.onCoachMarkClosed}
                position={COACHMARK_POSITION.TOP_RIGHT}
                text={t('Allow location tracking to find nearby locations')}
                isOpen={this.getCoachMarkStatus()}
                closeBtn={
                  <span className={clsx(Typo.overline, 'coachmark-close-btn')}>
                    {t('got it')}
                  </span>
                }
                disableLeaveListener
                disableEnterListener
              >
                {this.renderLocationTracking()}
              </Coachmark>
            </div>
            <ul className={searchResultClasses}>
              {loading && (
                <li className="search__result-item">
                  <i className="icon loader">
                    <Loader micro />
                  </i>
                  {t('Loading...')}
                </li>
              )}
              {suggestions &&
                suggestions.length > 0 &&
                suggestions.map((item, index) =>
                  this.renderSuggestion(item, index, getSuggestionItemProps),
                )}
            </ul>
          </React.Fragment>
        )}
      </PlacesAutocomplete>
    )
  }
}

SearchInput.propTypes = {
  geoUpdate: PropTypes.func,
  showUnavailable: PropTypes.func,
  t: PropTypes.func.isRequired,
  setCurrentLocation: PropTypes.func,
  setCoachMark: PropTypes.func,
  showCoachMark: PropTypes.bool,
}

SearchInput.defaultProps = {
  geoUpdate: undefined,
  showUnavailable: undefined,
  setCurrentLocation: undefined,
  setCoachMark: undefined,
  showCoachMark: false,
}

export { SearchInput }

const Extended = translate('global')(SearchInput)

export default Extended
